'use client';

import { Button } from '@zealot/ui/components/button';
import { Separator } from '@zealot/ui/components/separator';
import { SimpleDropdownMenu, type SimpleDropdownMenuItem } from '@zealot/ui/components/simple-dropdown-menu';
import { cn } from '@zealot/ui/lib/utils';
import { ChevronDown, Loader2, X } from 'lucide-react';

type DataTableBulkActionBarProps = {
  selectedCount: number;
  onClear: () => void;
  actions: SimpleDropdownMenuItem[];
  actionsLabel?: string;
  isPending?: boolean;
  pendingLabel?: string;
  className?: string;
};

const DataTableBulkActionBar = ({
  selectedCount,
  onClear,
  actions,
  actionsLabel = 'Actions',
  isPending = false,
  pendingLabel = 'Loading...',
  className,
}: DataTableBulkActionBarProps) => {
  if (selectedCount === 0) return null;

  return (
    <div
      data-slot="data-table-bulk-action-bar"
      role="toolbar"
      aria-label="Bulk actions"
      className={cn(
        'fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-card py-1.5 pr-1.5 pl-2 shadow-lg',
        className,
      )}
    >
      <span className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary text-sm">
        {selectedCount} selected
      </span>
      <Button variant="ghost" size="icon-sm" className="rounded-full" aria-label="Clear selection" onClick={onClear}>
        <X />
      </Button>
      <Separator orientation="vertical" className="h-6" />
      {isPending ? (
        <span className="flex items-center gap-2 px-3 text-muted-foreground text-sm">
          <Loader2 className="size-4 animate-spin" />
          {pendingLabel}
        </span>
      ) : (
        <SimpleDropdownMenu
          items={actions}
          side="top"
          align="end"
          trigger={
            <Button variant="outline" size="sm">
              {actionsLabel}
              <ChevronDown data-icon="inline-end" />
            </Button>
          }
        />
      )}
    </div>
  );
};

export type { DataTableBulkActionBarProps };
export { DataTableBulkActionBar };
